/* HUMAN MATTER — floating glass header.
   Scroll state, the liquid menu indicator (BYQ gem `gooey-nav-01` timings),
   the mega panels, the mobile tab menu, the cart-count bump and quick add.
   Everything is scoped to the <elora-header> element, so a section reload
   re-runs connectedCallback and nothing leaks.
   Keyboard: Space / ArrowDown on a menu link opens its panel and moves focus into it;
   Tab past the links keeps an open panel open until focus leaves the header; Escape closes
   and returns focus. The mobile menu takes the focus on open, keeps Tab inside the visible
   menu and gives the focus back to the burger on close. */

if (!customElements.get('elora-header')) {
  class EloraHeader extends HTMLElement {
    connectedCallback() {
      this.SETTLE_MS = 260;
      this.GRACE_MS = 130;

      this.section = this.closest('.shopify-section') || this.parentElement;
      this.bar = this.querySelector('.hmh__bar');
      this.nav = this.querySelector('[data-hmh-nav]');
      this.blob = this.querySelector('.hmh__blob');
      this.drop = this.querySelector('.hmh__drop');
      this.links = Array.from(this.querySelectorAll('.hmh__link'));
      this.panelsEl = this.querySelector('[data-hmh-panels]');
      this.panels = Array.from(this.querySelectorAll('[data-hmh-panel]'));
      this.tabsEl = this.querySelector('[data-hmh-tabs]');
      this.tabs = Array.from(this.querySelectorAll('[data-hmh-tab]'));
      this.burger = this.querySelector('[data-hmh-burger]');
      this.cartEl = this.querySelector('[data-hmh-cart]');
      this.threshold = parseInt(this.dataset.scrollThreshold, 10) || 40;
      this.openPanel = null;
      this.settleTimer = null;
      this.graceTimer = null;

      this.bind();
      this.onScroll();
      this.setHeights();
      if (this.section) this.section.classList.add('shopify-section-header-sticky');

      // the blob needs one paint before its target can be measured
      requestAnimationFrame(() => this.settleIndicator(false));
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScrollBound);
      window.removeEventListener('resize', this.onResizeBound);
      document.removeEventListener('click', this.onDocClickBound);
      document.removeEventListener('keydown', this.onKeyBound);
      window.clearTimeout(this.settleTimer);
      window.clearTimeout(this.graceTimer);
      document.body.classList.remove('overflow-hidden');
    }

    static get reduced() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    static get desktop() {
      return window.matchMedia('(min-width: 990px)').matches;
    }

    static string(key, fallback) {
      if (!EloraHeader.strings) {
        EloraHeader.strings = {};
        const el = document.getElementById('elora-strings');
        if (el) {
          try {
            EloraHeader.strings = JSON.parse(el.textContent) || {};
          } catch (e) {
            EloraHeader.strings = {};
          }
        }
      }
      return EloraHeader.strings[key] || fallback;
    }

    /* ----------------------------------------------------------- binding */

    bind() {
      this.onScrollBound = this.onScroll.bind(this);
      this.onResizeBound = this.onResize.bind(this);
      this.onDocClickBound = this.onDocClick.bind(this);
      this.onKeyBound = this.onKey.bind(this);

      window.addEventListener('scroll', this.onScrollBound, { passive: true });
      window.addEventListener('resize', this.onResizeBound);
      document.addEventListener('click', this.onDocClickBound);
      document.addEventListener('keydown', this.onKeyBound);

      this.links.forEach((link) => {
        link.addEventListener('mouseenter', () => {
          if (!EloraHeader.desktop) return;
          this.moveIndicator(link, true);
          this.handleTrigger(link);
        });
        // keyboard: moving to a plain link keeps an open panel open (Tab can still reach it)
        link.addEventListener('focus', () => {
          if (!EloraHeader.desktop) return;
          this.moveIndicator(link, true);
          if (link.dataset.hmhTrigger) this.handleTrigger(link);
        });
        link.addEventListener('keydown', (event) => {
          if (!EloraHeader.desktop || !link.dataset.hmhTrigger) return;
          if (event.key === ' ' || event.key === 'ArrowDown') {
            event.preventDefault();
            this.handleTrigger(link);
            this.focusPanel();
          }
        });
      });

      if (this.nav) {
        this.nav.addEventListener('mouseleave', () => {
          if (EloraHeader.desktop) this.settleIndicator(true);
        });
      }

      this.addEventListener('mouseenter', () => window.clearTimeout(this.graceTimer));
      this.addEventListener('mouseleave', () => {
        if (EloraHeader.desktop) this.scheduleClose();
      });
      this.addEventListener('focusout', (event) => {
        if (!EloraHeader.desktop) return;
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.closePanel();
        this.settleIndicator(false);
      });

      if (this.burger) {
        this.burger.addEventListener('click', () => {
          this.toggleMobile(!this.classList.contains('is-menu-open'));
        });
      }

      this.querySelectorAll('[data-hmh-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.toggleMobile(false));
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab));
      });

      this.querySelectorAll('[data-hmh-add]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          this.quickAdd(btn);
        });
      });
    }

    /* ------------------------------------------------------- scroll state */

    onScroll() {
      const y = window.pageYOffset || document.documentElement.scrollTop;
      this.classList.toggle('is-scrolled', y > this.threshold);
    }

    onResize() {
      this.setHeights();
      this.settleIndicator(false);
      if (this.openPanel) this.placePanel(this.openPanel.trigger, this.openPanel.panel);
      if (this.classList.contains('is-menu-open') && EloraHeader.desktop) this.toggleMobile(false);
    }

    setHeights() {
      const h = this.section ? this.section.offsetHeight : this.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${h}px`);
    }

    /* -------------------------------------------- liquid menu indicator */

    activeLink() {
      return this.querySelector('.hmh__link[aria-current="page"]');
    }

    settleIndicator(animate) {
      const active = this.activeLink();
      if (active) this.moveIndicator(active, animate);
      else this.hideIndicator();
    }

    hideIndicator() {
      if (!this.blob) return;
      this.blob.style.setProperty('--hmh-w', '0px');
      if (this.drop) this.drop.style.setProperty('--hmh-ds', '0');
    }

    moveIndicator(link, animate) {
      if (!this.blob || !this.nav) return;

      const navRect = this.nav.getBoundingClientRect();
      const rect = link.getBoundingClientRect();
      if (!rect.width) return;

      const x = rect.left - navRect.left;
      const centre = x + rect.width / 2;

      this.blob.style.setProperty('--hmh-x', `${x}px`);
      this.blob.style.setProperty('--hmh-w', `${rect.width}px`);

      window.clearTimeout(this.settleTimer);
      if (!this.drop) return;

      this.drop.style.setProperty('--hmh-dx', `${centre}px`);

      if (animate && !EloraHeader.reduced && this.dataset.indicator === 'liquid') {
        this.drop.style.setProperty('--hmh-ds', '1');
        this.settleTimer = window.setTimeout(() => {
          this.drop.style.setProperty('--hmh-ds', '0');
        }, this.SETTLE_MS);
      } else {
        this.drop.style.setProperty('--hmh-ds', '0');
      }
    }

    /* --------------------------------------------------------- mega panels */

    panelFor(name) {
      return this.panels.find((panel) => panel.dataset.hmhPanel === name) || null;
    }

    handleTrigger(link) {
      const name = link.dataset.hmhTrigger;
      if (!name) {
        this.closePanel();
        return;
      }
      const panel = this.panelFor(name);
      if (panel) this.showPanel(link, panel);
    }

    placePanel(trigger, panel) {
      if (!this.panelsEl || !this.bar || !trigger) return;
      const wrapRect = this.panelsEl.getBoundingClientRect();
      const barRect = this.bar.getBoundingClientRect();
      const rect = trigger.getBoundingClientRect();
      const width = panel.offsetWidth || 380;

      const min = barRect.left - wrapRect.left;
      let max = barRect.right - wrapRect.left - width;
      if (max < min) max = min;

      let x = rect.left - wrapRect.left - 12;
      if (x < min) x = min;
      if (x > max) x = max;

      panel.style.setProperty('--hmh-mega-x', `${Math.round(x)}px`);
    }

    showPanel(trigger, panel) {
      window.clearTimeout(this.graceTimer);
      if (this.openPanel && this.openPanel.panel === panel) return;
      this.closePanel();
      if (EloraHeader.desktop) this.placePanel(trigger, panel);
      panel.classList.add('is-open');
      if (trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');
      this.openPanel = { trigger, panel };
    }

    closePanel() {
      if (!this.openPanel) return;
      this.openPanel.panel.classList.remove('is-open');
      if (this.openPanel.trigger.hasAttribute('aria-expanded')) {
        this.openPanel.trigger.setAttribute('aria-expanded', 'false');
      }
      this.openPanel = null;
    }

    focusPanel() {
      if (!this.openPanel) return;
      const first = this.focusable(this.openPanel.panel)[0];
      if (first) first.focus();
    }

    focusable(root) {
      return Array.from(
        root.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null && !el.closest('[aria-hidden="true"]'));
    }

    scheduleClose() {
      window.clearTimeout(this.graceTimer);
      this.graceTimer = window.setTimeout(() => {
        this.closePanel();
        this.settleIndicator(true);
      }, this.GRACE_MS);
    }

    onDocClick(event) {
      if (this.contains(event.target)) return;
      this.closePanel();
      if (this.classList.contains('is-menu-open')) this.toggleMobile(false);
    }

    onKey(event) {
      if (event.key === 'Tab' && this.classList.contains('is-menu-open')) {
        this.keepFocusInMenu(event);
        return;
      }
      if (event.key !== 'Escape') return;
      if (this.classList.contains('is-menu-open')) {
        this.toggleMobile(false);
        return;
      }
      if (!this.openPanel) return;
      const { trigger } = this.openPanel;
      // focus first: the trigger's focus handler would reopen a panel closed a line earlier
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
      this.closePanel();
      this.settleIndicator(true);
    }

    /* ---------------------------------------------------------- mobile menu */

    menuFocusables() {
      // what a keyboard user can see while the menu is open: the tab row, the open panel, the footer
      const parts = [this.tabsEl, this.openPanel && this.openPanel.panel, this.querySelector('[data-hmh-mobfoot]')].filter(Boolean);
      return parts.reduce((all, part) => all.concat(this.focusable(part)), []);
    }

    keepFocusInMenu(event) {
      const items = this.menuFocusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const inside = items.indexOf(document.activeElement) !== -1;
      if (!inside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    toggleMobile(open) {
      const wasOpen = this.classList.contains('is-menu-open');
      this.classList.toggle('is-menu-open', open);
      if (this.burger) this.burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (this.tabsEl) this.tabsEl.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.body.classList.toggle('overflow-hidden', open);

      if (open) {
        const first = this.tabs.find((tab) => tab.tagName === 'BUTTON');
        if (first) this.activateTab(first);
        const target = first || this.tabs[0] || this.querySelector('[data-hmh-close]');
        if (target) requestAnimationFrame(() => target.focus());
      } else {
        this.closePanel();
        this.tabs.forEach((tab) => tab.classList.remove('is-active'));
        if (wasOpen && this.burger && this.contains(document.activeElement)) this.burger.focus();
      }
    }

    activateTab(tab) {
      const name = tab.dataset.hmhTab;
      if (!name) return;
      this.tabs.forEach((item) => item.classList.toggle('is-active', item === tab));
      const panel = this.panelFor(name);
      if (panel) this.showPanel(tab, panel);
    }

    /* -------------------------------------------------- cart count + add */

    cartCount() {
      if (!this.cartEl) return 0;
      const bubble = this.cartEl.querySelector('.cart-count-bubble [aria-hidden="true"]');
      return bubble ? parseInt(bubble.textContent.trim(), 10) || 0 : 0;
    }

    previewSuffix(url) {
      // The store's primary domain has no DNS, so a preview session must keep
      // `_fd=0` on every request or Shopify 302s it to the dead domain.
      if (window.location.search.indexOf('_fd=') === -1) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + '_fd=0';
    }

    showAddError(button, message) {
      const tile = button.closest('.hmh__tile') || button.parentElement;
      let note = tile.querySelector('.hmh__add-err');
      if (!note) {
        note = document.createElement('span');
        note.className = 'hmh__add-err';
        note.setAttribute('role', 'alert');
        tile.appendChild(note);
      }
      note.textContent = message || EloraHeader.string('quick_add_error', 'Could not add this item. Please try again.');
      window.clearTimeout(note.timer);
      note.timer = window.setTimeout(() => note.remove(), 6000);
    }

    quickAdd(button) {
      const id = button.dataset.hmhAdd;
      if (!id || button.getAttribute('aria-busy') === 'true') return;

      const drawer = document.querySelector('cart-drawer');
      const cartItems = typeof CartItems !== 'undefined' ? CartItems : null;
      if ((cartItems && (cartItems.activeUpdate || cartItems.draining || cartItems.pendingChanges?.size)) ||
        (drawer && (drawer.retrying || drawer.reading || drawer.pendingAdd || drawer.headerAddActive ||
          drawer.classList.contains('is-adding'))) || window.HMLpBonus?.active) return;
      if (drawer && (drawer.needsSync || drawer.unconfirmed)) {
        // An earlier write is uncertain: read the real cart first, never replay this paid add automatically.
        if (!drawer.reading && !drawer.retrying && typeof drawer.retryRead === 'function') drawer.retryRead();
        return;
      }

      const bonus = window.HMLpBonus;
      const bonusUI = window.HMLpBonusUI;
      const addForm = typeof FormData === 'function' ? new FormData() : null;
      if (addForm) {
        addForm.append('id', String(id));
        addForm.append('quantity', '1');
      }
      const bonusEligible = Boolean(bonus && addForm && typeof bonus.isEligibleForm === 'function' &&
        (bonus.isEligibleForm(addForm) || bonus.relevant));
      const bonusToken = bonusEligible && typeof bonus.begin === 'function' ? bonus.begin() : null;
      if (bonusEligible && !bonusToken) return;

      let payload = { items: [{ id: Number(id), quantity: 1 }] };

      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        payload.sections = drawer.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
      }

      const bundledAdd = bonusToken && drawer && Array.isArray(payload.sections) && bonusUI && typeof bonusUI.prepareAdd === 'function'
        ? bonusUI.prepareAdd(addForm) : null;
      if (bundledAdd) payload = { ...bundledAdd, sections: payload.sections, sections_url: payload.sections_url };

      button.setAttribute('aria-busy', 'true');
      if (drawer) drawer.headerAddActive = true;
      const url = this.previewSuffix((window.routes && window.routes.cart_add_url) || '/cart/add');
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const abortTimer = controller ? window.setTimeout(() => controller.abort(), 12000) : null;

      const operation = Promise.resolve()
        .then(() => fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(payload),
          ...(controller ? { signal: controller.signal } : {}),
        }))
        .then(async (serverResponse) => {
          let data = null;
          let isJson = false;
          try {
            const text = typeof serverResponse.text === 'function' ? await serverResponse.text() : JSON.stringify(await serverResponse.json());
            data = JSON.parse(text);
            isJson = !!data && typeof data === 'object' && !Array.isArray(data);
          } catch (_) { /* an HTML challenge, empty body, or broken JSON is unconfirmed */ }
          const status = Number(serverResponse && serverResponse.status) || 0;
          const ok = typeof serverResponse?.ok === 'boolean' ? serverResponse.ok : status >= 200 && status < 300;
          if (!isJson) throw Object.assign(new Error(`cart/add answered ${status} without JSON`), { unconfirmed: true, status });
          if (status >= 500 || !status) {
            throw Object.assign(new Error(`cart/add answered ${status}`), { unconfirmed: true, status });
          }
          if (bundledAdd && (!ok || data.status)) {
            throw Object.assign(new Error('Bundled add was not fully confirmed'), { unconfirmed: true, status });
          }
          if (!data || data.status) {
            // Shopify refused (sold out, limit reached…): say so where the click happened, and tell the cart helpers
            bonusUI?.cancelCheckout?.();
            this.showAddError(button, data && (data.description || data.message));
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartError) {
              publish(PUB_SUB_EVENTS.cartError, { source: 'elora-header', kind: 'refused', productVariantId: id, message: data && data.message });
            }
            return false;
          }
          if (!ok) {
            bonusUI?.cancelCheckout?.();
            this.showAddError(button, data.description || data.message);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartError) {
              publish(PUB_SUB_EVENTS.cartError, { source: 'elora-header', kind: 'refused', productVariantId: id, status });
            }
            return false;
          }

          let response = drawer?.cartFromResponse?.(data) || data;
          if (bonusToken && bonusUI && typeof bonusUI.settle === 'function') {
            response = await bonusUI.settle(response, drawer ? drawer.getSectionsToRender().map((section) => section.id) : [],
              bundledAdd ? addForm : undefined);
          }
          this.closePanel();
          if (this.classList.contains('is-menu-open')) this.toggleMobile(false);

          let sectionsRenderedBySource = false;
          if (drawer && typeof drawer.renderContents === 'function') {
            // Dawn's renderContents clears `is-empty` on .drawer__inner but not on
            // <cart-drawer> itself, so its focus trap then reads a null container
            // (and the filled drawer keeps empty-cart styling). Clear it first.
            // A reply without usable `sections` is the renderer's job too: it fetches them itself.
            drawer.classList.remove('is-empty');
            const renderResult = await drawer.renderContents(response);
            if (renderResult === false) throw Object.assign(new Error('Cart drawer sections were not rendered'), { unconfirmed: true });
            sectionsRenderedBySource = typeof drawer.sectionsComplete === 'function' && drawer.sectionsComplete(response.sections);
          } else {
            // no cart drawer on this page — refresh the count only
            this.refreshCount();
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'elora-header', productVariantId: id,
              sectionsRenderedBySource,
            });
          }
          try { localStorage.setItem('noom-cart-revision', `${Date.now()}:${Math.random()}`); } catch (_) { /* private mode */ }
          return true;
        })
        .catch((error) => {
          if (bonusToken && bonusUI) bonusUI.markDirty?.();
          bonusUI?.cancelCheckout?.();
          if (drawer && typeof drawer.setNeedsSync === 'function') drawer.setNeedsSync(true);
          this.showAddError(button);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartError) {
            const reason = error && error.name === 'AbortError' ? 'timeout' : error && error.unconfirmed ? 'change' : 'network';
            publish(PUB_SUB_EVENTS.cartError, { source: 'elora-header', kind: 'unconfirmed', reason,
              status: error && error.status, productVariantId: id, quantity: 1 });
          }
          return false;
        })
        .finally(() => {
          if (cartItems && cartItems.activeUpdate === operation) cartItems.activeUpdate = null;
          if (drawer) drawer.headerAddActive = false;
          if (bonusToken && typeof bonus.end === 'function') bonus.end(bonusToken);
          if (bonusUI && typeof bonusUI.render === 'function') bonusUI.render();
          if (abortTimer) window.clearTimeout(abortTimer);
          button.removeAttribute('aria-busy');
        });

      if (cartItems) cartItems.activeUpdate = operation;
      return operation;
    }

    refreshCount() {
      const url = this.previewSuffix(`${(window.routes && window.routes.cart_url) || '/cart'}.js`);
      fetch(url, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          if (!cart || typeof cart.item_count !== 'number' || !this.cartEl) return;
          if (cart.item_count === 0) {
            // Oddit rec. 2 (Kamil 05.09): an empty bag shows no count at all
            const empty = this.cartEl.querySelector('.cart-count-bubble');
            if (empty) empty.remove();
            return;
          }
          let wrap = this.cartEl.querySelector('.cart-count-bubble');
          if (!wrap) {
            wrap = document.createElement('div');
            wrap.className = 'cart-count-bubble';
            this.cartEl.appendChild(wrap);
          }
          let bubble = wrap.querySelector('[aria-hidden="true"]');
          if (!bubble) {
            bubble = document.createElement('span');
            bubble.setAttribute('aria-hidden', 'true');
            wrap.appendChild(bubble);
          }
          bubble.textContent = cart.item_count;
          // the same spoken text Dawn's cart-icon-bubble section renders ("N items")
          let hidden = wrap.querySelector('.visually-hidden');
          if (!hidden) {
            hidden = document.createElement('span');
            hidden.className = 'visually-hidden';
            wrap.appendChild(hidden);
          }
          const template = cart.item_count === 1 ? EloraHeader.string('cart_count_one', '1 item') : EloraHeader.string('cart_count_other', '2 items');
          hidden.textContent = template.replace(/\d+/, String(cart.item_count));
        })
        .catch(() => {
          /* ignore */
        });
    }
  }

  customElements.define('elora-header', EloraHeader);
}
